import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactFormSchema } from "../shared/schema";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function ContactForm() {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data) => {
      return apiRequest("POST", "/api/contact", data);
    },
    onSuccess: () => {
      setIsSubmitted(true);
      form.reset();
      toast({
        title: "¡Mensaje enviado!",
        description: "Gracias por contactarnos. Te responderemos pronto.",
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Hubo un problema al enviar el mensaje. Inténtalo de nuevo.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data) => {
    submitMutation.mutate(data);
  };

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 md:p-8" data-testid="form-contact">
      <h2 className="text-2xl md:text-3xl font-heading font-semibold text-[#033644] mb-6">
        Envíanos un mensaje
      </h2>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Tu nombre completo"
                    data-testid="input-name"
                  />
                </FormControl>
                <FormMessage data-testid="error-name" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="tu@email.com"
                    data-testid="input-email"
                  />
                </FormControl>
                <FormMessage data-testid="error-email" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mensaje *</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={5}
                    placeholder="Cuéntanos sobre tu proyecto..."
                    data-testid="input-message"
                    className="resize-none"
                  />
                </FormControl>
                <FormMessage data-testid="error-message" />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={submitMutation.isPending}
            className="w-full bg-[#FFA500] text-white border-[#FF8C00]"
            data-testid="button-submit"
          >
            {submitMutation.isPending ? "Enviando..." : "Enviar Mensaje"}
          </Button>

          {isSubmitted && (
            <div
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg"
              data-testid="success-message"
            >
              ¡Mensaje enviado con éxito!
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}